import clsx from 'clsx';
import React, { useState, useRef, useEffect } from 'react';

import { FLOW_KEY_FEEDBACK } from '../../../../../../../../store/dashboard/askAica/constants';
import { GLOBAL_ASK_AICA_COPY } from '../../../../../../../../store/globalAskAica/constants';
import { ASK_BACK_KIND } from '../../../../../../../../store/globalAskAica/types';
import { normaliseAskBackSlots } from '../../../../../../../../store/globalAskAica/askBackSlots';
import { AgentInput } from '../../../../../../../../store/dashboard/askAica/actions';
import { ClaimedAttachment } from '../../../../../../../../store/dashboard/askAica/types';
import { useAskAicaSurface, ASK_AICA_SURFACE } from '../../context/AskAicaSurfaceContext';
import { Mixpanel } from '../../../../../../../../utils/mixpanel';
import { AICA_ASK_AICA_FEEDBACK_SENT } from '../../../../../../../../utils/constants/mixpanelEvents/investorEvents';

import MagicUploadIcon from '../../../../../../../../assets/Svg/MagicuploadIcon';
import AskAicaGreeting from '../AskAicaGreeting/AskAicaGreeting';
import CompanyChip from '../CompanyChip/CompanyChip';
import CompanyPicker from '../CompanyPicker/CompanyPicker';
import ClarificationCard from '../ClarificationCard/ClarificationCard';
import MentionDropdown from './MentionDropdown';
import { useFeedbackMention, stripMention } from './useFeedbackMention';
import { useComposerAttachments } from './useComposerAttachments';
import AttachmentChips from '../AttachmentChips/AttachmentChips';

import StopChatIcon from '../../../../../../../../assets/Svg/StopChatIcon';
import AttachmentIcon from '../../../../../../../../assets/Svg/AttachmentIcon';
import { CHAT_ATTACHMENT_COPY } from '../../../../../../../../store/dashboard/askAica/constants';

import styles from './AskAicaInputBox.module.scss';

// Re-exported for tests that unit-test the pure detector against the component.
export { containsCompleteMention } from './useFeedbackMention';

interface AskAicaInputBoxProps {
    onSendMessage: (
        message: string,
        attachmentDocIds?: string[],
        flowKey?: string,
        agentInput?: AgentInput,
        /** The settled chips, so the turn's bubble can show its cards before the final frame lands. */
        pendingUploads?: ClaimedAttachment[],
    ) => (() => void) | undefined;
    isLoading: boolean;
    // Mobile full-screen surface (RECUR-27483): the screen supplies its own
    // headline and pins the input to the bottom, so skip the desktop empty-state
    // greeting + centering and apply the mobile input styling instead.
    mobileLayout?: boolean;
    // Mobile-only "✦ Suggestions" pill inside the shell — toggles the screen's
    // suggestion strip (rendered by the parent above the input).
    suggestionsToggle?: { open: boolean; onToggle: () => void };
    // Shared abort holder. Sends that bypass this input (suggestion pills,
    // question badges) return an abort fn the internal ref never sees — the
    // parent stores those here so the stop button can cancel ANY stream.
    abortRef?: React.MutableRefObject<(() => void) | undefined>;
}

const AskAicaInputBox: React.FC<AskAicaInputBoxProps> = ({
    onSendMessage,
    isLoading = false,
    mobileLayout = false,
    suggestionsToggle,
    abortRef,
}) => {
    const internalAbortRef = useRef<(() => void) | undefined>(undefined);
    const abortFunctionRef = abortRef ?? internalAbortRef;

    const {
        messages,
        isLoadingChatHistory,
        selectedSources,
        surface,
        enableFeedbackMention,
        askBack,
        currentCompany,
        replyToGate,
        dismissAskBack,
        clearCompany,
        hasInProgressTurn,
        hasPendingClarification: surfaceHasPendingClarification,
        pendingClarification,
        answerClarification,
        stopRun,
    } = useAskAicaSurface();
    const hasPendingClarification = surfaceHasPendingClarification ?? false;

    // A run in flight, whether this tab started it or re-attached to it after a
    // reload. Stop stays live for the whole of it, `cancelling` included: a second
    // press is an idempotent no-op and is the only recovery from a stranded run.
    // A round waiting on the user counts too: the composer is not an answer channel while the
    // docked card is open, and Stop is how the user abandons it.
    const isTurnInFlight = isLoading || hasInProgressTurn || hasPendingClarification;

    const [message, setMessage] = useState('');
    const { attachments, fileInputRef, openPicker, handleFileInputChange, handleDragOver, handleDrop } =
        useComposerAttachments(isTurnInFlight);

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const backdropRef = useRef<HTMLDivElement>(null);

    const {
        feedbackMode: rawFeedbackMode,
        showMentionDropdown: rawShowMentionDropdown,
        syncDropdown,
        autoSpaceMention,
        handleMentionSpaceKey,
        completeMentionInline,
        handleMentionKey,
        buildBackdropHtml,
        feedbackHasContent,
    } = useFeedbackMention(message, setMessage, textareaRef, styles.MentionHighlight);

    // @chuckNorris feedback-mention is leads-only — always inert on the
    // portfolio surface, regardless of what the text happens to contain.
    const feedbackMode = enableFeedbackMention && rawFeedbackMode;
    const showMentionDropdown = enableFeedbackMention && rawShowMentionDropdown;
    // Auto-resize textarea. For an empty field the correct height is a single row
    // (rows={1} + min-height); deriving it from scrollHeight here can over-report
    // during the modal's open animation on first paint, rendering the composer tall
    // until the first keystroke re-measures. Only pin scrollHeight when there IS
    // content — the multi-line growth path is unchanged; empty = one line on both
    // the leads and portfolio surfaces (visually identical to before for leads,
    // which already rendered one line for an empty field).
    useEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        if (message) {
            el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
        }
    }, [message]);

    // Auto-focus textarea when component mounts
    useEffect(() => {
        const focusTimeout = setTimeout(() => {
            textareaRef.current?.focus();
        }, 350);
        return () => clearTimeout(focusTimeout);
    }, []);

    const stopChat = async () => {
        // The server call is what actually cancels the run — a client
        // disconnect no longer does. Its cancellation teardown pushes a
        // synthetic final frame down the SAME connection, so the local abort
        // must stay off the happy path: aborting here would throw that frame
        // away before it can settle the message status and render "Generation
        // stopped". Only fall back to the local abort if the server call
        // itself failed — the run may not have been stopped at all.
        let stopped = false;
        try {
            stopped = stopRun ? await stopRun() : false;
        } catch (error) {
            console.error('Error stopping chat:', error);
        } finally {
            if (!stopped) abortFunctionRef.current?.();
            abortFunctionRef.current = undefined;
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const caretPos = e.target.selectionStart ?? e.target.value.length;
        // A keystroke that just completed a hand-typed @chuckNorris gets its
        // separating space here, so the popover and the stored value agree on it.
        const { value, caret } = autoSpaceMention(e.target.value, caretPos);
        setMessage(value);
        // Pass the caret so a partial typed mid-string (not just at the end)
        // still opens the popover.
        syncDropdown(value, caret);
    };

    const handleScroll = () => {
        if (backdropRef.current && textareaRef.current) {
            backdropRef.current.scrollTop = textareaRef.current.scrollTop;
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (isTurnInFlight) {
            stopChat();
            return;
        }

        if (feedbackMode) {
            // Strip the @chuckNorris trigger before sending — it is a routing
            // affordance, not feedback content. A-15: block if nothing remains.
            const textWithoutMention = stripMention(message);
            if (!textWithoutMention) return;
            Mixpanel.track(AICA_ASK_AICA_FEEDBACK_SENT, {
                messageLength: textWithoutMention.length,
                sourcesCount: selectedSources?.length,
            });
            // Feedback is an independent message — no answer link.
            abortFunctionRef.current = onSendMessage(textWithoutMention, undefined, FLOW_KEY_FEEDBACK);
            setMessage('');
            return;
        }

        if (message.trim() || attachments.attachmentDocIds.length) {
            const { attachmentDocIds } = attachments;
            abortFunctionRef.current = attachmentDocIds.length
                ? onSendMessage(message.trim(), attachmentDocIds, undefined, undefined, attachments.pendingUploads)
                : onSendMessage(message.trim());
            setMessage('');
            attachments.clear();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        // Let the mention popover consume its own keys (Enter/Tab/Esc) first.
        if (handleMentionKey(e)) return;
        // Then drop a space that would only duplicate the mention's auto space.
        if (handleMentionSpaceKey(e)) return;

        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            // Enter must respect the same gate as the send button (e.g. attachments
            // must have settled) — do not let it bypass canSend.
            if (canSend) handleSubmit(e);
        }
    };

    // In feedback mode: require non-empty text beyond the mention itself (A-15).
    const canSend = feedbackMode
        ? feedbackHasContent
        : (message.trim().length > 0 || attachments.attachmentDocIds.length > 0) && attachments.allSettled;

    if (messages.length === 0 && isLoadingChatHistory) return <></>;

    return (
        <>
            {/* Sibling of the card, not a child: .InputArea is positioned, so it paints
                over this rect's lower half — the grey reads as sitting behind it. As a
                child it could only paint on top, and z-index:-1 sinks it behind the
                whole panel (.FloatingAskBar is a stacking context). */}
            {/* Both the picker and the chip are the grey rect BEHIND the composer card
                (Figma 32804:43164 / 32555:25342) — siblings, not children, so .InputArea
                paints over their lower half. See CompanyChip.module.scss. */}
            {/* Only kind:"pick" renders here. A kind:"clarify" ask-back is prose,
                answered by typing in the composer, and streams as an ordinary
                assistant bubble instead (RECUR-27889) — so it deliberately has no
                branch in this region. */}
            {/* The pending round docks first, so on portfolio it stacks above the CompanyChip. A pick
                ask-back never coexists with it (the gate never runs on a resume), so there is
                no ordering to define against CompanyPicker. */}
            {pendingClarification && (
                <ClarificationCard
                    key={pendingClarification.tool_use_id}
                    card={pendingClarification}
                    onSubmit={(answers) => {
                        abortFunctionRef.current = answerClarification?.(pendingClarification, answers);
                    }}
                    disabled={isLoading}
                />
            )}
            {surface === ASK_AICA_SURFACE.PORTFOLIO && askBack?.kind === ASK_BACK_KIND.PICK && (
                <CompanyPicker
                    // Normalised here so the component never sees which Athena
                    // produced the payload: `slots` when RECUR-27888 is deployed,
                    // a legacy `candidates`-only array folded into one slot until
                    // then (see askBackSlots.ts::normaliseAskBackSlots).
                    slots={normaliseAskBackSlots(askBack).slots}
                    onConfirm={(picked) => replyToGate?.(picked)}
                    onDismiss={dismissAskBack}
                    disabled={isLoading || hasPendingClarification}
                />
            )}
            {surface === ASK_AICA_SURFACE.PORTFOLIO && currentCompany && (
                <CompanyChip
                    companyName={currentCompany.name || GLOBAL_ASK_AICA_COPY.UNKNOWN_COMPANY}
                    onClear={() => clearCompany?.()}
                    disabled={hasPendingClarification}
                />
            )}
            <div
                className={clsx(styles.InputArea, {
                    [styles.InputCentered]: !mobileLayout && messages.length === 0 && !isLoadingChatHistory,
                    [styles.MobileLayout]: mobileLayout,
                    [styles.JoinedToCard]: !!pendingClarification,
                })}
            >
                {!mobileLayout && messages.length === 0 && !isLoadingChatHistory && (
                    <div className={styles.EmptyText}>
                        <AskAicaGreeting />
                    </div>
                )}

                {/* Portfolio-only docked region (net-new — RECUR-27369): the ask_back
                    picker and the resolved-company chip sit directly above the
                    composer, never as a chat bubble. Refinement (no free-text row
                    on the picker itself, RECUR-27369 redesign) happens by typing a
                    normal message in the composer below — the gate's /search
                    resolves it via the regular onSendMessage path further down. */}
                <form
                    onSubmit={handleSubmit}
                    className={styles.InputForm}
                    onDragOver={mobileLayout ? undefined : handleDragOver}
                    onDrop={mobileLayout ? undefined : handleDrop}
                >
                    {!mobileLayout && (
                        <AttachmentChips items={attachments.items} onRemove={attachments.removeItem} disabled={isTurnInFlight} />
                    )}
                    <div className={styles.InputWrapper}>
                        {!mobileLayout && (
                            <>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    hidden
                                    onChange={handleFileInputChange}
                                    data-testid="ask-aica-file-input"
                                />
                                <button
                                    type="button"
                                    className={styles.AttachButton}
                                    onClick={openPicker}
                                    disabled={isTurnInFlight}
                                    aria-label={CHAT_ATTACHMENT_COPY.ATTACH_LABEL}
                                    data-testid="ask-aica-attach-button"
                                >
                                    <AttachmentIcon width="18" height="18" />
                                </button>
                            </>
                        )}
                        {showMentionDropdown && <MentionDropdown onConfirm={completeMentionInline} />}
                        {/* TextareaContainer holds the backdrop overlay and the textarea stacked */}
                        <div className={styles.TextareaContainer}>
                            <div
                                ref={backdropRef}
                                className={styles.MentionBackdrop}
                                aria-hidden="true"
                                dangerouslySetInnerHTML={{ __html: buildBackdropHtml(message) }}
                            />
                            <textarea
                                ref={textareaRef}
                                value={message}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                                onScroll={handleScroll}
                                placeholder="Ask anything"
                                className={styles.MessageInput}
                                disabled={isTurnInFlight}
                                rows={1}
                                data-testid="ask-aica-textarea"
                            />
                        </div>
                        <button
                            type="submit"
                            className={`${styles.SendButton} ${isTurnInFlight || canSend ? styles.Active : ''}`}
                            disabled={!isTurnInFlight && !canSend}
                            data-testid="ask-aica-send-button"
                        >
                            {isTurnInFlight ? <StopChatIcon /> : <span className={styles.SendIcon}>→</span>}
                        </button>
                    </div>
                </form>

                {mobileLayout && suggestionsToggle && (
                    <div className={styles.MobileActionsRow}>
                        <button
                            type="button"
                            className={clsx(styles.SuggestionsToggle, {
                                [styles.SuggestionsToggleActive]: suggestionsToggle.open,
                            })}
                            onClick={suggestionsToggle.onToggle}
                            aria-pressed={suggestionsToggle.open}
                            data-testid="suggestions-toggle-pill"
                        >
                            <MagicUploadIcon
                                colour={suggestionsToggle.open ? 'var(--primary-purple)' : 'var(--table-heading-text-color)'}
                                height="14"
                                width="14"
                            />
                            Suggestions
                        </button>
                    </div>
                )}
            </div>
            <div className={styles.WarningText}>AICA can make mistakes. Check important info</div>
        </>
    );
};

export default AskAicaInputBox;
